import { Injectable } from '@angular/core'
import { Apollo } from 'apollo-angular'
import { DocumentNode } from 'graphql'
import { filter } from 'graphql-anywhere'
import gql from 'graphql-tag'
import { BehaviorSubject, Subscription } from 'rxjs'
import { query as DashboardPageQuery } from '../../pages/dashboard-page/dashboard.queries'
import { map } from 'rxjs/operators'
import { errorToast } from '../../toaster'

@Injectable()
export default class SubscriptionDataService {

  _observables = {}
  proxySubject = new BehaviorSubject(null);
  mutateSub: Subscription
  priceList = `
    priceList{
      id
      creditPriceInCents
      name
      products{
        id
        productId
        code
        words
        valueInCredits
        addOns{
          id
          addOnId
          code
          valueInCredits
        }
      }
      services{
        id
        serviceId
        code
        forSubsWithCreditsUpTo
        valueInCents
      }
    }
  `

  customerPriceListInfo = gql`
  fragment CustomerPriceListInfo on Customer {
    ${this.priceList}
  }
  `

  subscriptionPriceListInfo = gql`
  fragment SubscriptionPriceListInfo on MultipassSubscription {
    priceList{
      id
      creditPriceInCents
      name
      products{
        id
        productId
        code
        words
        valueInCredits
        addOns{
          id
          addOnId
          code
          valueInCredits
        }
      }
      services{
        id
        serviceId
        code
        forSubsWithCreditsUpTo
        valueInCents
      }
    }
  }
  `

  customerInfo = gql`
  fragment CustomerInfo on Customer {
    id
    multipass
    canBeMigratedFromExpired
    businessName
    expiresAt
    couponApplications {
      id
      expiresAt
      usable
      future
      coupon {
        id
        externalName
        couponCode
        discountType
        duration
        expiryType
        discountInCents
        discountInDollars
        discountPercent
        dollarLimit
      }
    }
    account {
      id
      agency
    }
    subscription {
      __typename
      id
      state
      nextCreditsIssuedAt
      pastDueAt
      quantity
      currentPeriodEndsAt
      ... on MultipassSubscription {
        id
        durationInMonths
        selectedProducts{
          id
          code
          quantity
          selectedAddOns {
            id
            code
            quantity
          }
        }
        scheduledToCancelAt
        ...SubscriptionPriceListInfo
      }
    }
    scheduledSubscription {
      __typename
      id
      ... on MultipassSubscription {
        id
        currentPeriodStartedAt
        durationInMonths
        selectedProducts{
          id
          code
          quantity
          selectedAddOns {
            id
            code
            quantity
          }
        }
      }
    }
    reactivationSubscription {
      __typename
      id
      ... on MultipassSubscription {
        id
        currentPeriodStartedAt
        durationInMonths
        selectedProducts{
          id
          code
          quantity
          selectedAddOns {
            id
            code
            quantity
          }
        }
      }
    }
    ...CustomerPriceListInfo
  }
  ${this.customerPriceListInfo}
  ${this.subscriptionPriceListInfo}
  `

  subscriptionsInfo = gql`
  fragment SubscriptionsInfo on Query {
    customer (id: $customerId){
      ...CustomerInfo
    }
  }
  ${this.customerInfo}
  `

  priceListInfo = gql`
  fragment PriceListInfo on Query {
    priceList (id: $priceListId){
      id
      creditPriceInCents
      name
      products{
        id
        productId
        code
        words
        valueInCredits
        addOns{
          id
          addOnId
          code
          valueInCredits
        }
      }
      services{
        id
        serviceId
        code
        forSubsWithCreditsUpTo
        valueInCents
      }
    }
  }
 `

  planConfigQuery = gql`
  query PlanConfigQuery($customerId: ID!) {
    ...SubscriptionsInfo
  }
  ${this.subscriptionsInfo}
  `

  priceListQuery = gql`
    query PriceListQuery($priceListId: Int!) {
      ...PriceListInfo
    }
    ${this.priceListInfo}
  `

  constructor(private apollo: Apollo){}

  executeQuery(variables: { customerId?: number, priceListId?: number } = {}): void {
    const { customerId } = variables
    const query = customerId ? this.planConfigQuery : this.priceListQuery

    this.apollo
    .watchQuery<any>({
      query,
      variables,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network'
    })
    .valueChanges
    .subscribe(({data}) => {
      if (data) {
        this.proxySubject.next(data)
      }
    })
  }

  getSubscriptiontObserver() {
    return this.getFragmentObserver('SubscriptionsInfo', this.subscriptionsInfo)
  }

  getPriceListObserver() {
    return this.getFragmentObserver('CustomerPriceListInfo', this.customerPriceListInfo)
  }

  getFragmentObserver(fragmentName: string, fragment: DocumentNode) {
    if (!this._observables[fragmentName]) {
      this._observables[fragmentName] = new BehaviorSubject(null)
    }
    this.proxySubject.subscribe((data) => {
      this._observables[fragmentName].next(filter(fragment, data))
    })
    return this._observables[fragmentName]
  }

  createSubscription(fragment: any, customerId: any, subscription: any): Promise<any> {
    const createSubscriptionMutation = gql`
    mutation CreateMultipassSubscriptionMutation($input: CreateMultipassSubscriptionMutationInput!) {
      CreateMultipassSubscriptionMutation(input: $input) {
        errors
        customer{
          ...CustomerInfo
        }
      }
    }
    ${this.customerInfo}
    `

    return new Promise<any>((resolve, reject) => {
      if (this.mutateSub) this.mutateSub.unsubscribe()
      this.mutateSub = this.apollo.mutate({
        mutation: createSubscriptionMutation,
        variables: {
          input: {
            customerId,
            subscription,
          }
        },
        refetchQueries: [
          {
            query: DashboardPageQuery,
            variables: { customerId },
          },
        ]
      })
      .pipe(map(({data}) => data['CreateMultipassSubscriptionMutation']))
      .subscribe(data => {
        const { customer, errors } = data
        if (errors?.length) {
          reject(errors)
        } else if (data) {
          resolve(data)
        }
      }, error => {
        errorToast(error)
        reject(error)
      })
    })
  }

  reactivateSubscription(fragment: any, customerId: any, saveAsPending: boolean = false): Promise<any> {
    const reactivateSubscriptionMutation = gql`
    mutation ReactivateMultipassSubscriptionMutation($input: ReactivateMultipassSubscriptionMutationInput!) {
      ReactivateMultipassSubscriptionMutation(input: $input) {
        errors
        customer{
          ...CustomerInfo
        }
      }
    }
    ${this.customerInfo}
    `

    return new Promise<any>((resolve, reject) => {
      if (this.mutateSub) this.mutateSub.unsubscribe()
      this.mutateSub = this.apollo.mutate({
        mutation: reactivateSubscriptionMutation,
        variables: {
          input: {
            customerId,
            saveAsPending,
          }
        },
        refetchQueries: [
          {
            query: DashboardPageQuery,
            variables: { customerId },
          },
        ]
      })
      .pipe(map(({data}) => data['ReactivateMultipassSubscriptionMutation']))
      .subscribe(data => {
        const { customer, errors } = data
        if (errors?.length) {
          reject(errors)
        } else if (data) {
          resolve(data)
        }
      }, error => {
        errorToast(error)
        reject(error)
      })
    })
  }

  cancelScheduledMultipassSubscriptionMutation(customerId: any): Promise<any> {
    const cancelScheduledMultipassSubscriptionMutation = gql`
    mutation CancelScheduledMultipassSubscriptionMutation($input: CancelScheduledMultipassSubscriptionMutationInput!) {
      CancelScheduledMultipassSubscriptionMutation(input: $input) {
        errors
        customer{
          ...CustomerInfo
        }
      }
    }
    ${this.customerInfo}
    `

    return new Promise<any>((resolve, reject) => {
      if (this.mutateSub) this.mutateSub.unsubscribe()
      this.mutateSub = this.apollo.mutate({
        mutation: cancelScheduledMultipassSubscriptionMutation,
        variables: {
          input: {
            customerId,
          }
        }
      }).pipe(map((result: any) => result.data.CancelScheduledMultipassSubscriptionMutation)).subscribe(({ errors, customer }) => {
        if (errors.length > 0) {
          errors.forEach(e => errorToast(e))
          reject()
        } else if (customer) {
          resolve(customer)
        }
      })
    })
  }

  undoCancelMultipassSubscriptionMutation(customerId: any): Promise<any> {
    const undoCancelMultipassSubscriptionMutation = gql`
    mutation UndoCancelMultipassSubscriptionMutation($input: UndoCancelMultipassSubscriptionMutationInput!) {
      UndoCancelMultipassSubscriptionMutation(input: $input) {
        errors
        customer{
          ...CustomerInfo
        }
      }
    }
    ${this.customerInfo}
    `

    return new Promise<any>((resolve, reject) => {
      if (this.mutateSub) this.mutateSub.unsubscribe()
      this.mutateSub = this.apollo.mutate({
        mutation: undoCancelMultipassSubscriptionMutation,
        variables: {
          input: {
            customerId,
          }
        }
      }).subscribe(resolve)
    })
  }

}
